
import { mdiFeather } from '@mdi/js'
import Button from '@mui/material/Button'
import { HelpToggleButton } from 'components/App/components/HelpToggleButton'
import MonthPicker from 'components/CalendarView/components/MonthPicker'
import WeekNav from 'components/CalendarView/components/WeekNav'
import { NOTIFICATION_DURATION_LONG } from 'components/ConnectedSnackbar'
import EmptyView from 'components/EmptyView'
import PostFilesDialog from 'components/Posts/PostFilesDialog'
import PPSelect from 'components/PPSelect'
import SimpleLoader from 'components/SimpleLoader'
import useConditionalEffect from 'hooks/useConditionalEffect'
import { PostedPost } from 'interfaces'
import { POST_TYPES } from 'interfaces/Content/ContentType'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useLocation, useNavigate, useParams } from 'react-router'
import { PostedSortBy, PostedSortMenu, SortDirection } from 'routes/publishing/components/BulkActionsMenus'
import CopyPostsPopup from 'routes/publishing/components/CopyPostsPopup'
import PostedFilter from 'routes/publishing/components/PostedFilter'
import { ERROR_NO_PROFILES_SUBTITLE, ERROR_NO_PROFILES_TITLE, PostErrorView } from 'routes/publishing/components/PostErrorView'
import RefreshStatsButton from 'routes/publishing/components/RefreshStatsButton'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, concatMap, filter, map, switchMap, tap } from 'rxjs/operators'
import { getDerivedStateFromPost, getFacebookPostTemplates, mergeState, resetComposer } from 'services/compose'
import { connectedDestinationsSelector } from 'services/destinations'
import { setCalendarViewMode } from 'services/post'
import { fetchPostedPostsForRange, getPostedPosts, requeuePost } from 'services/post/actions'
import { postedAction, PostedPostsAction } from 'services/post/archive/actions'
import {
  calendarMonthStartSelector,
  calendarWeekStartSelector,
  postsUISelector,
  selectedProfilesPPIdsSelector,
  selectedProfilesSelector
} from 'services/post/selectors'
import { forceFetchExternalPosts, toggleExternalPostsVisible } from 'services/post/toggleExternal/actions'
import { checkFeatureAvailability } from 'services/product'
import { message } from 'services/snackbar'
import { HS_EVENT_POSTS_SORTED, trackHubspotEvent } from 'services/tracking/hubspot'
import { isBetaUserSelector } from 'services/users/selectors'
import { BRAND_FACEBOOK, ContentType } from 'shared'
import {
  FB_POST_TYPE_REEL,
  FEATURE_POST_COPY,
  FEATURE_POST_INSIGHTS_ACTIONS_REPLAN,
  FEATURE_POST_INSIGHTS_ACTIONS_SORT,
  NETWORKS_NAMES
} from 'shared/constants'
import { PostedPostsSearchParams } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import { getMonthViewDays, getMonthViewDaysByWeeks, getWeekViewDays } from 'utils/calendar'
import { DAYS_IN_WEEK, WEEKDAY_LABELS } from 'utils/calendar/constants'
import { addPosts, setPosts, updatePost } from './actions'
import { ActiveViewSelector } from './components/ActiveViewSelector'
import CalendarViewOptions from './components/CalendarViewOptions'
import { HistoryPostsTimeRange } from './components/HistoryRangePicker/HistoryRangePicker'
import PostedPostsList from './components/PostedPostsList'
import { PostsCalendarView, WeekSelection } from './components/PostsCalendarView'
import PostsFilter from './components/PostsFilter'
import { FavoritesFilter } from './components/PostsFilter/PostsFilter'
import { PostsGridView } from './components/PostsGridView/PostsGridView'
import { pruneSelectionByProfiles } from './postSelection'
import styles from './PostViews.pcss'
import { PostsAction, postsReducer } from './reducer'
import { usePostIdsRef } from './usePostIdsRef'

type SearchFilters = {
  query: string,
  types: ContentType[],
  searchInArticleSummary?: boolean,
  favorites: 'all' | 'favorites' | 'non-favorites'
}

export function PostedPostsManager({ isArchivedView }: { isArchivedView?: boolean }) {
  const intl = useIntl()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch<StoreThunkDispatch>()
  // TEMP: Beta feature flag for actions in PostedPosts
  const isBetaUser = useSelector(isBetaUserSelector)
  const params = useParams()
  const [activeView, setActiveView] = useState<'list' | 'grid' | 'calendar'>('list')
  const selectedProfiles = useSelector(selectedProfilesSelector)
  const connectedProfiles = useSelector(connectedDestinationsSelector)
  const calendarMode = useSelector(postsUISelector).calendarMode
  const weekStartDay = useSelector(calendarWeekStartSelector)
  const monthStart = useSelector(calendarMonthStartSelector)
  const fetchIdsString = useMemo(() => selectedProfiles.map(p => p.idNew).join(','), [selectedProfiles])
  const ppids = useSelector(selectedProfilesPPIdsSelector).join(',')
  const [externalVisible, setExternalVisible] = useState<boolean | undefined>(undefined)
  const [sortBy, setSortBy] = useState<PostedSortBy>(PostedSortBy.Time)
  const [sortDirection, setSortDirection] = useState<SortDirection>('descending')
  const [posts, postsDispatch] = useReducer<PostedPost[], [PostsAction]>(postsReducer, [])
  const postsRef = useRef<PostedPost[]>(posts)
  const postsIdsRef = usePostIdsRef(posts)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(true)
  const [totalPostsCount, setTotalPostsCount] = useState<null | number>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [postInMediaPreviewDialog, setPostInMediaPreviewDialog] = useState<PostedPost | null>(null)
  const [search, setSearch] = useState<SearchFilters>(
    { query: '', types: POST_TYPES, searchInArticleSummary: true, favorites: 'all' }
  )
  const [selectedPostsIds, setSelectedPostsIds] = useState<Record<string, boolean> | 'all'>({})
  const [postsToCopy, setPostsToCopy] = useState<PostedPost[]>([])
  const [rangeStartDate, setRangeStartDate] = useState<string | null>(null)
  const [rangeEndDate, setRangeEndDate] = useState<string | null>(null)
  const [range, setRange] = useState<HistoryPostsTimeRange>('all')

  const fetch$ = useRef<Subject<PostedPostsSearchParams & { sort: PostedSortBy }>>(undefined)
  const fetchByDate$ = useRef<Subject<{ ppids: string[], startDate: string, endDate: string }>>(undefined)
  const toggleExternalPostedVisible$ = useRef<Subject<string>>(undefined)
  const forceExternalPostsRefresh$ = useRef<Subject<string>>(undefined)
  const requeue$ = useRef<Subject<{
    postIds: string[], ppids: string[], all: boolean, query?: string, types?: ContentType[]
  }>>(undefined)
  type Action = {
    action: PostedPostsAction,
    postIds: string[],
    ppids: string[],
    all: boolean,
    query?: string,
    types?: ContentType[]
  }
  const action$ = useRef<Subject<Action>>(undefined)
  const selectionRef = useRef<{ postIds: string[], profileIds: string[], ppids: string[], all: boolean }>(undefined)
  const postProfileIdsRef = useRef<Record<string, string>>({})

  const now = new Date()
  const [selectedYear, setSelectedYear] = useState(now.getFullYear())
  const [weekSelection, setWeekSelection] = useState<WeekSelection>({ date: now, offset: 0 })
  const [selectedMonthIndex, setSelectedMonth] = useState(now.getMonth())
  const startMonthIndex = useRef(now.getMonth())
  const startYear = useRef(now.getFullYear())

  useEffect(() => {
    // NOTE: Fetch TOBI templates to use when rendering <PostPreviewText> in posted posts
    dispatch(getFacebookPostTemplates()).unwrap()
  }, [dispatch])

  useEffect(() => {
    // EXPL: keep latest posts in a ref so item callbacks stay referentially stable
    // across posts updates and don't force every list item to re-render
    postsRef.current = posts
  }, [posts])

  useEffect(() => {
    // EXPL: remember the profile of every post loaded so far, so the selection can still be
    // pruned by profile after `posts` has been reset by a profile selection change
    posts.forEach(post => {
      postProfileIdsRef.current[post.id] = post.profileId
    })
  }, [posts])

  useEffect(() => {
    fetch$.current = new Subject<PostedPostsSearchParams & { sort: PostedSortBy }>()
    fetch$.current
      .pipe(tap(({ page }) => {
        setLoading(true)
        setPage(page)
      })).pipe(
        switchMap((params) => {
          const sortString = params.sort ? PostedSortBy[params.sort].toLowerCase() : undefined
          return dispatch(getPostedPosts({ ...params, sortBy: sortString })).pipe(
            catchError((e) => {
              console.log(e)
              dispatch(message('Error fetching posts! Please refresh.', 'error'))
              return observableOf({ posts: [], error: true })
            })
          )
        }))
      .subscribe((response: any) => {
        if (!response.error) {
          setHasNextPage(response.posts.length > 0)
          setExternalVisible(response.showExternal)
          if (response.page === 0) {
            setTotalPostsCount(response.totalPostsCount)
            postsDispatch(setPosts(response.posts))
          } else {
            postsDispatch(addPosts(response.posts))
          }
        }
        setLoading(false)
      })

    return () => fetch$.current?.unsubscribe()
  }, [dispatch])

  useEffect(() => {
    fetchByDate$.current = new Subject<{ ppids: string[], startDate: string, endDate: string }>()
    fetchByDate$.current
      .pipe(
        filter(({ ppids }) => ppids.length > 0),
        tap(() => {
          setLoading(true)
        })
      ).pipe(
        switchMap(({ ppids, startDate, endDate }) => {
          return dispatch(fetchPostedPostsForRange(ppids, startDate, endDate))
        }))
      .pipe(
        catchError((e) => {
          console.log(e)
          dispatch(message('Error fetching posts! Please refresh.', 'error'))
          return observableOf({ posts: [], error: true })
        })
      )
      .subscribe((response: any) => {
        if (!response.error) {
          setTotalPostsCount(response.totalPostsCount)
          postsDispatch(setPosts(response.posts))
        }
        setLoading(false)
      })
  }, [dispatch])

  useEffect(() => {
    toggleExternalPostedVisible$.current = new Subject<string>()
    toggleExternalPostedVisible$.current
      .pipe(concatMap(ppid => dispatch(toggleExternalPostsVisible(ppid))
        .pipe(catchError(() => {
          dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
          return observableOf({ error: true })
        }))
      ))
      .subscribe((response) => {
        if (!response.error) {
          setRefreshKey(current => current + 1)
        }
      })

    return () => toggleExternalPostedVisible$.current?.unsubscribe()
  }, [dispatch, intl])

  useEffect(() => {
    forceExternalPostsRefresh$.current = new Subject<string>()
    forceExternalPostsRefresh$.current
      .pipe(concatMap(ppid => dispatch(forceFetchExternalPosts(ppid))
        .pipe(catchError(() => {
          dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
          return observableOf({ error: true })
        }))
      ))
      .subscribe((response) => {
        if (!response.error) {
          setRefreshKey(current => current + 1)
          dispatch(message(intl.formatMessage({ id: 'post.hint.fetching-posted-insights' })))
        }
      })

    return () => forceExternalPostsRefresh$.current?.unsubscribe()
  }, [dispatch, intl])

  useEffect(() => {
    requeue$.current = new Subject<{ postIds: string[], ppids: string[], all: boolean, query?: string, types?: ContentType[] }>()
    requeue$.current
      .pipe(concatMap(({ ppids, postIds, all, query, types }) => {
        const startDate = rangeStartDate || undefined
        const endDate = rangeEndDate || undefined
        return dispatch(requeuePost(
          postIds, ppids, isArchivedView ? 'archived' : 'posted', all, undefined, query, types, startDate, endDate
        ))
          .pipe(
            catchError((error) => {
              if (error.message) {
                dispatch(message(error.message, 'error', NOTIFICATION_DURATION_LONG))
              } else {
                dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
              }
              return observableOf({ error })
            })
          )
      }))
      .subscribe((response: any) => {
        if (!response.error) {
          dispatch(message(intl.formatMessage({ id: 'notifications.post-replanned' }), 'success'))
        }
      })

    return () => requeue$.current?.unsubscribe()
  }, [dispatch, intl, rangeStartDate, rangeEndDate, isArchivedView])

  useEffect(() => {
    action$.current = new Subject<Action>()
    action$.current
      .pipe(concatMap(({ action, ppids, postIds, all, query, types }) => {
        return dispatch(postedAction(action, postIds, ppids, all, undefined, query, types))
          .pipe(
            catchError((error: any) => {
              const msg = error.response?.error.message || error.message
              if (msg) {
                dispatch(message(msg, 'error', NOTIFICATION_DURATION_LONG))
              } else {
                dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
              }
              return observableOf({ error })
            }),
            map((response: any) => ({ ...response, action }))
          )
      }))
      .subscribe((response: any) => {
        if (!response.error) {
          const postIds = response.postIds
          if (postIds && postIds.length > 0 && (response.action === 'archive' || response.action === 'unarchive')) {
            setRefreshKey(current => current + 1)
            setSelectedPostsIds({})
          }
          dispatch(message(intl.formatMessage({
            id: `notifications.posts-${response.action === 'archive' ? 'archived' : 'unarchived'}`
          }), 'success'))
        }
      })

    return () => action$.current?.unsubscribe()
  }, [dispatch, intl, posts])

  useEffect(() => {
    if (activeView !== 'calendar' && fetchIdsString) {
      postsDispatch(setPosts([]))
      fetch$.current?.next({
        page: 0,
        pageIds: fetchIdsString.split(','),
        sort: sortBy,
        sortDirection,
        query: search.query,
        types: search.types,
        startDate: rangeStartDate,
        endDate: rangeEndDate,
        favorites: search.favorites,
        searchInArticleSummary: search.searchInArticleSummary,
        archivedOnly: isArchivedView
      })
    }
  }, [
    fetchIdsString,
    sortBy,
    refreshKey,
    search.query,
    search.types,
    sortDirection,
    rangeStartDate,
    rangeEndDate,
    search.searchInArticleSummary,
    activeView,
    search.favorites,
    isArchivedView
  ])

  useEffect(() => {
    const profileIds = fetchIdsString.split(',')
    const ppids = Object.values(connectedProfiles).filter(p => profileIds.includes(p.idNew)).map(p => p.ppid)
    if (selectedPostsIds === 'all') {
      selectionRef.current = {
        all: true,
        postIds: posts.map(p => p.id),
        profileIds,
        ppids
      }
    } else {
      selectionRef.current = {
        all: false,
        profileIds,
        ppids,
        postIds: Object.keys(selectedPostsIds).filter(id => selectedPostsIds[id])
      }
    }
  }, [selectedPostsIds, posts, fetchIdsString, connectedProfiles])

  // Deselect posts that no longer belong to any of the selected profiles
  useConditionalEffect(([_connectedProfiles]) => {
    const profileIds = fetchIdsString ? fetchIdsString.split(',') : []
    const activeProfileIds = Object.values(_connectedProfiles).filter(p => profileIds.includes(p.idNew)).map(p => p.id)
    setSelectedPostsIds(current => pruneSelectionByProfiles(current, postProfileIdsRef.current, activeProfileIds))
  }, [fetchIdsString], [connectedProfiles])

  // Reset selection on view change. Default archived view to 'list' if calendar is selected
  useConditionalEffect(([_activeView]) => {
    setSelectedPostsIds({})
    selectionRef.current = undefined
    if (isArchivedView && _activeView === 'calendar') {
      setActiveView('list')
    }
  }, [isArchivedView, navigate], [activeView])

  const togglePostSelected = useCallback((id: string) => {
    setSelectedPostsIds(current => {
      if (current === 'all') {
        return postsRef.current.reduce((selection: Record<string, boolean>, post) => {
          selection[post.id] = post.id !== id
          return selection
        }, {})
      }
      return { ...current, [id]: !current[id] }
    })
  }, [postsIdsRef])

  const toggleAllSelected = useCallback(() => {
    setSelectedPostsIds(current => {
      if (current === 'all') {
        return {}
      }
      return 'all'
    })
  }, [])

  const onToggleFavorite = useCallback((postId: string, ppid: string, value: boolean) => {
    const action = value ? 'favorite' : 'unfavorite'
    return dispatch(postedAction(action, [postId], [ppid])).pipe(tap(() => {
      postsDispatch(updatePost({ id: postId, isFavorite: value }))
    }))
  }, [dispatch])

  const loadNextPage = useCallback(() => {
    if (hasNextPage && !loading && fetchIdsString) {
      fetch$.current?.next({
        page: page + 1,
        pageIds: fetchIdsString.split(','),
        sort: sortBy,
        sortDirection,
        query: search.query,
        types: search.types,
        startDate: rangeStartDate,
        endDate: rangeEndDate,
        archivedOnly: isArchivedView,
        favorites: search.favorites,
        searchInArticleSummary: search.searchInArticleSummary
      })
    }
  }, [
    hasNextPage,
    loading,
    fetchIdsString,
    page,
    sortBy,
    sortDirection,
    search.query,
    search.types,
    search.searchInArticleSummary,
    search.favorites,
    isArchivedView,
    rangeStartDate,
    rangeEndDate
  ])

  const closePostMediaPreviewDialog = useCallback(() => {
    setPostInMediaPreviewDialog(null)
  }, [])

  // EXPL: Reset externalVisible when switching profiles. It must be set only when posts are fetched
  const profileId = selectedProfiles[0]?.id
  useEffect(() => {
    setExternalVisible(undefined)
  }, [profileId])

  useEffect(() => {
    // NOTE: check top level path and only redirect to default view if on History page.
    // This is to avoid redirecting from Composer when profile selection is changed
    if (window.location.pathname.startsWith('/history') && selectedProfiles.length > 1 && !isArchivedView) {
      navigate('/history/posted/post-planner', { replace: true })
    }
  }, [selectedProfiles.length, navigate, location.pathname, isArchivedView])

  useEffect(() => {
    const ppid = selectedProfiles[0]?.ppid
    if (!ppid) {
      return
    }

    if (params.type === 'post-planner' && externalVisible) {
      toggleExternalPostedVisible$.current?.next(ppid)
      return
    }

    if (params.type === 'all-publishers' && externalVisible === false) {
      dispatch(message(intl.formatMessage({ id: 'post.hint.fetching-posted-insights' })))
      toggleExternalPostedVisible$.current?.next(ppid)
    }
  }, [params.type, externalVisible, dispatch, selectedProfiles, intl])

  const onToggleExternalPostedVisible = useCallback(() => {
    if (params.type === 'post-planner') {
      navigate('/history/posted/all-publishers', { replace: true })
    } else {
      navigate('/history/posted/post-planner', { replace: true })
    }
  }, [navigate, params.type])

  const onForceExternalPostsRefresh = useCallback(() => {
    const profile = selectedProfiles[0]
    const ppid = profile?.ppid
    forceExternalPostsRefresh$.current?.next(ppid)
  }, [selectedProfiles])

  const onSortChange = useCallback((value: PostedSortBy) => {
    const sortAvailable = dispatch(checkFeatureAvailability(FEATURE_POST_INSIGHTS_ACTIONS_SORT))
    if (!sortAvailable) {
      return
    }

    // If changing the sort key, reset page to 0
    if (value !== sortBy) {
      setSortBy(value)
      dispatch(trackHubspotEvent(HS_EVENT_POSTS_SORTED))
    }
  }, [sortBy, dispatch])

  const isExternalFbReel = useCallback((post?: PostedPost) => {
    return Boolean(post?.isExternal && post.network === BRAND_FACEBOOK && post.specialType === FB_POST_TYPE_REEL)
  }, [])

  const notifyExternalFbReelBlocked = useCallback(() => {
    dispatch(message(intl.formatMessage({ id: 'post.notifications.external-fb-reel-action-unsupported' }), 'warning'))
  }, [dispatch, intl])

  const onRequeue = useCallback((id: string, ppid: string) => {
    const requeueAvailable = dispatch(checkFeatureAvailability(FEATURE_POST_INSIGHTS_ACTIONS_REPLAN))
    if (!requeueAvailable) {
      return
    }

    if (isExternalFbReel(postsRef.current.find(p => p.id === id))) {
      notifyExternalFbReelBlocked()
      return
    }

    requeue$.current?.next({ postIds: [id], ppids: [ppid], all: false })
  }, [dispatch, isExternalFbReel, notifyExternalFbReelBlocked])

  const requeuePosts = useCallback(() => {
    const requeueAvailable = dispatch(checkFeatureAvailability(FEATURE_POST_INSIGHTS_ACTIONS_REPLAN))
    if (!requeueAvailable || selectionRef.current?.postIds.length === 0) {
      return
    }

    const selectedIds = selectionRef.current?.postIds || []
    const currentPosts = postsRef.current
    const selectedPosts = selectionRef.current?.all ? currentPosts : currentPosts.filter(p => selectedIds.includes(p.id))
    if (selectedPosts.some(isExternalFbReel)) {
      notifyExternalFbReelBlocked()
      return
    }

    requeue$.current?.next({
      postIds: selectedIds,
      ppids: selectionRef.current?.ppids || [],
      all: Boolean(selectionRef.current?.all),
      query: search.query,
      types: search.types
    })
  }, [dispatch, search.query, search.types, isExternalFbReel, notifyExternalFbReelBlocked])

  const onArchive = useCallback((id: string, ppid: string) => {
    action$.current?.next({ action: 'archive', postIds: [id], ppids: [ppid], all: false })
  }, [])

  const onBulkArchive = useCallback(() => {
    if (selectionRef.current?.postIds.length === 0) {
      return
    }
    action$.current?.next({
      postIds: selectionRef.current?.postIds || [],
      ppids: selectionRef.current?.ppids || [],
      all: Boolean(selectionRef.current?.all),
      query: search.query,
      types: search.types,
      action: 'archive'
    })
  }, [search.query, search.types])

  const onRepost = useCallback((post: PostedPost) => {
    if (isExternalFbReel(post)) {
      notifyExternalFbReelBlocked()
      return
    }
    dispatch(resetComposer())
    dispatch(mergeState(getDerivedStateFromPost(post, 'posted')))
    navigate(
      '/composer',
      {
        state: {
          floating: true,
          modal: 'compose',
          opener: location.pathname + location.search,
          background: location
        }
      }
    )
  }, [dispatch, location, navigate, isExternalFbReel, notifyExternalFbReelBlocked])

  const onFilterPosts = useCallback(
    (query: string, types: ContentType[], searchInArticleSummary?: boolean, favType?: FavoritesFilter) => {
      setSearch({ query, types, searchInArticleSummary, favorites: favType || 'all' })
    }, []
  )

  const onCopyPosts = useCallback(() => {
    const copyAllowed = dispatch(checkFeatureAvailability(FEATURE_POST_COPY))
    if (copyAllowed) {
      if (selectionRef.current?.all) {
        setPostsToCopy(postsRef.current)
      } else if (selectionRef.current?.postIds) {
        setPostsToCopy(postsRef.current.filter(p => selectionRef.current?.postIds.includes(p.id)))
      }
    }
  }, [dispatch])

  const closeCopyPostsPopup = useCallback(() => {
    setPostsToCopy([])
    setSelectedPostsIds({})
  }, [])

  const onCopyPost = useCallback((post: PostedPost) => {
    const copyAllowed = dispatch(checkFeatureAvailability(FEATURE_POST_COPY))
    if (copyAllowed) {
      setPostsToCopy([post])
    }
  }, [dispatch])

  const onSelectedRangeChange = (value: HistoryPostsTimeRange, start?: string, end?: string) => {
    setRange(value)
    setRangeStartDate(start || null)
    setRangeEndDate(end || null)
  }

  const sortMenuDisabled = selectedProfiles.length !== 1 || posts.length === 0 || loading || selectedProfiles[0]?.type !== BRAND_FACEBOOK

  const selectedPostsCount = useMemo(() => {
    return selectedPostsIds !== 'all'
      ? Object.values(selectedPostsIds).filter(Boolean).length
      : totalPostsCount || 0
  }, [totalPostsCount, selectedPostsIds])

  const bulkActionsProps = useMemo(() => {
    return {
      withRequeue: true,
      withCopy: true,
      hidePostActions: false,
      allSelected: selectedPostsIds === 'all',
      disabled: selectedPostsCount === 0,
      selectedCount: selectedPostsCount,
      onBulkRequeue: requeuePosts,
      onToggleAllSelected: toggleAllSelected,
      onCopy: onCopyPosts,
      onArchive: isArchivedView ? undefined : onBulkArchive
    }
  }, [selectedPostsIds, selectedPostsCount, requeuePosts, toggleAllSelected, onCopyPosts, isArchivedView, onBulkArchive])

  const weekDays = useMemo(() => {
    const startDayIndex = WEEKDAY_LABELS.indexOf(weekStartDay)
    return getWeekViewDays(new Date(), weekSelection.offset, startDayIndex)
  }, [weekSelection.offset, weekStartDay])

  const monthViewStartsFromCurrentWeek = useMemo(() => {
    return monthStart === 'this-week' && selectedMonthIndex === startMonthIndex.current && selectedYear === startYear.current
  }, [monthStart, selectedMonthIndex, selectedYear])

  const monthVisibleDays = useMemo(() => {
    const startDayIndex = WEEKDAY_LABELS.indexOf(weekStartDay)
    if (monthViewStartsFromCurrentWeek) {
      return getMonthViewDaysByWeeks(new Date(), startDayIndex)
    }
    return getMonthViewDays(selectedYear, selectedMonthIndex, WEEKDAY_LABELS.indexOf(weekStartDay))
  }, [monthViewStartsFromCurrentWeek, selectedMonthIndex, selectedYear, weekStartDay])

  useEffect(() => {
    if (activeView !== 'calendar') {
      return
    }
    let startDate: string | undefined
    let endDateObj: Date | undefined

    if (calendarMode === 'week') {
      if (weekDays.length === 0) return
      startDate = weekDays[0].date.toISOString()
      endDateObj = new Date(weekDays[weekDays.length - 1].date.getTime())
    } else {
      if (monthVisibleDays.length === 0) return
      startDate = monthVisibleDays[0].date.toISOString()
      endDateObj = monthVisibleDays[monthVisibleDays.length - 1]?.date
    }

    if (!endDateObj) {
      return
    }

    endDateObj.setHours(23, 59, 59) // eslint-disable-line no-magic-numbers
    const endDate = endDateObj.toISOString()
    const ppidsArray = calendarMode === 'week' ? ppids.split(',').filter(Boolean) : ppids.split(',').filter(Boolean)

    if (ppidsArray.length > 0) {
      fetchByDate$.current?.next({
        ppids: ppidsArray,
        startDate,
        endDate
      })
    }
  }, [ppids, monthVisibleDays, activeView, calendarMode, weekDays])

  const goToToday = () => {
    setSelectedYear(now.getFullYear())
    setSelectedMonth(now.getMonth())
    setWeekSelection({ date: now, offset: 0 })
  }

  const onSelectedMonthChange = (year: number, month: number) => {
    setSelectedYear(year)
    setSelectedMonth(month)
  }

  const selectNextWeek = useCallback(() => {
    setWeekSelection(current => {
      const nextDate = new Date(current.date.getTime())
      nextDate.setDate(nextDate.getDate() + DAYS_IN_WEEK)
      return {
        date: nextDate,
        offset: current.offset + 1
      }
    })
  }, [])

  const selectPrevWeek = useCallback(() => {
    setWeekSelection(current => {
      const nextDate = new Date(current.date.getTime())
      nextDate.setDate(nextDate.getDate() - DAYS_IN_WEEK)
      return {
        date: nextDate,
        offset: current.offset - 1
      }
    })
  }, [])

  const onSelectedCalendarModeChange = (mode: 'month' | 'week') => {
    goToToday()
    dispatch(setCalendarViewMode(mode))
  }

  return (
    <div>
      {activeView !== 'calendar' && (
        <div className={styles['header-posted']}>
          <ActiveViewSelector
            value={activeView}
            onChange={setActiveView}
            className={styles['view-switch']}
            hideOptions={isArchivedView ? ['calendar'] : []}
          />
          {!isArchivedView && (
            <PostedFilter
              selectedProfiles={selectedProfiles}
              externalPostedVisible={Boolean(externalVisible)}
              className={styles['posted-via']}
              toggleExternalPostedVisible={onToggleExternalPostedVisible}
              onRefresh={onForceExternalPostsRefresh}
            />
          )}
          <PostsFilter
            activeTypes={search.types}
            range={range}
            // disable favorites filter in archived view
            favoritesFilter={isArchivedView ? undefined : search.favorites}
            onRangeChange={onSelectedRangeChange}
            onFilter={onFilterPosts}
          />
          <div className={styles['align-right']}>
            {typeof totalPostsCount === 'number' && (
              <div className={styles.count}>
                <FormattedMessage id="post.planned.posts-count-label" values={{ count: totalPostsCount }} />
              </div>
            )}
            {!isArchivedView && <RefreshStatsButton selectedProfiles={selectedProfiles} />}
            <PostedSortMenu
              sortBy={sortBy}
              sortDirection={sortDirection}
              network={selectedProfiles[0]?.type}
              disabled={sortMenuDisabled}
              disabledMessage={
                selectedProfiles.length !== 1
                  ? intl.formatMessage({ id: 'post.notifications.sort-by-single-profile' })
                  : selectedProfiles[0]?.type !== BRAND_FACEBOOK
                    ? intl.formatMessage(
                      { id: 'post.notifications.sort-unsupported-network' },
                      { network: NETWORKS_NAMES[selectedProfiles[0]?.type] }
                    )
                    : undefined
              }
              className={styles['posted-sort-menu']}
              onSortChange={onSortChange}
              onSortDirectionChange={setSortDirection}
            />
            <HelpToggleButton />
          </div>
        </div>
      )}
      {activeView === 'calendar' && !isArchivedView && (
        <div className={styles['header-calendar']}>
          <CalendarViewOptions
            postsCount={totalPostsCount}
            withPreview={false}
            mode="posted"
            activeView={activeView}
            onActiveViewChange={setActiveView}
          />
          <div className={styles['header-bottom']}>
            <Button onClick={goToToday}>
              <FormattedMessage id="post.calendar.labels.today" />
            </Button>
            <div className={styles.middle}>
              {calendarMode === 'month' ? (
                <MonthPicker
                  startMonthIndex={startMonthIndex.current}
                  selectedMonthIndex={selectedMonthIndex}
                  selectedYear={selectedYear}
                  minYear={1900}
                  disableFutureMonths
                  onSelectionChange={onSelectedMonthChange}
                />
              ) : (
                <WeekNav
                  days={weekDays}
                  withBackNav
                  withForwardNav={weekSelection.offset < 0}
                  onNext={selectNextWeek}
                  onPrev={selectPrevWeek}
                />
              )}
            </div>
            <div className={styles['options-r']}>
              <PPSelect
                name="View"
                options={{
                  month: { label: <FormattedMessage id="post.calendar.labels.month" /> },
                  week: { label: <FormattedMessage id="post.calendar.labels.week" /> }
                }}
                selectedValue={calendarMode}
                className={styles.select}
                id="calendar-view-select"
                onSelectionChange={onSelectedCalendarModeChange as any}
              />
            </div>
          </div>
        </div>
      )}
      <div className={`${styles.content} ${styles['content-posted']} ${sortBy !== PostedSortBy.Time ? styles['with-padding'] : ''}`}>
        {activeView === 'list' && (
          <PostedPostsList
            posts={posts}
            sortBy={sortBy}
            selectedPosts={selectedPostsIds}
            bulkActionsProps={bulkActionsProps}
            actionsClassName={styles['actions-posted']}
            onLoadNextPage={loadNextPage}
            onMediaPreviewBtnClick={setPostInMediaPreviewDialog}
            onRepost={onRepost}
            onRequeuePosted={onRequeue}
            onCopy={onCopyPost}
            onTogglePostSelected={togglePostSelected}
            onToggleFavorite={onToggleFavorite}
            onArchive={onArchive}
          />
        )}
        {activeView === 'grid' && (
          <PostsGridView
            posts={posts}
            selectedPosts={selectedPostsIds}
            bulkActionsProps={bulkActionsProps}
            withDragAndDrop={false}
            onLoadNextPage={loadNextPage}
            onTogglePostSelected={togglePostSelected}
            onCopyPost={onCopyPost}
            onRequeuePost={onRequeue}
            onRepost={onRepost}
            onArchive={onArchive}
            onToggleFavorite={onToggleFavorite}
          />
        )}
        {activeView === 'calendar' && (
          <PostsCalendarView
            year={selectedYear}
            mode={calendarMode}
            monthIndex={selectedMonthIndex}
            posts={posts as any}
            loading={loading}
            selection={weekSelection}
            dimPastDays={false}
            className={styles['calendar-box']}
            onPostClick={console.log}
          />
        )}
        {!loading && selectedProfiles.length === 0 && (
          <PostErrorView title={ERROR_NO_PROFILES_TITLE} subtitle={ERROR_NO_PROFILES_SUBTITLE} />
        )}
        {!loading && selectedProfiles.length !== 0 && posts.length === 0 && (
          <EmptyView
            icon={mdiFeather}
            title={`No ${isArchivedView ? 'archived posts' : 'posted posts'}`}
            subtitle={(
              <div>
                <NavLink
                  to={{ pathname: '/composer' }}
                  state={{ floating: true, modal: 'compose', opener: location.pathname, background: location }}
                >
                  Create a new post
                </NavLink>
                &nbsp;or&nbsp;<NavLink to="/content/search/content">search for content</NavLink>
              </div>
            )}
            backdrop
          />
        )}
        {loading && activeView !== 'calendar' && <SimpleLoader small />}
      </div>
      <PostFilesDialog
        active={Boolean(postInMediaPreviewDialog)}
        images={postInMediaPreviewDialog?.images || []}
        onClose={closePostMediaPreviewDialog}
      />
      <CopyPostsPopup
        open={postsToCopy.length > 0}
        posts={postsToCopy as any[]}
        allSelected={Boolean(selectionRef.current?.all)}
        query={search.query}
        types={search.types}
        postType={isArchivedView ? 'archived' : 'posted'}
        onClose={closeCopyPostsPopup}
        onCopyStarted={closeCopyPostsPopup}
      />
    </div>
  )
}

export default PostedPostsManager
